import { default as Input } from 'antd/es/input';
import { default as Modal } from 'antd/es/modal';
import type { ReactNode } from 'react';
import { Controller } from 'react-hook-form';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';

import {
  CREATE_MCP_CANCEL_LABEL,
  CREATE_MCP_CANCEL_TEST_ID,
  CREATE_MCP_ERROR_TEST_ID,
  CREATE_MCP_FORM_ID,
  CREATE_MCP_GENERIC_ERROR,
  CREATE_MCP_MODAL_TEST_ID,
  CREATE_MCP_NAME_ERROR_TEST_ID,
  CREATE_MCP_NAME_LABEL,
  CREATE_MCP_NAME_PLACEHOLDER,
  CREATE_MCP_NAME_TEST_ID,
  CREATE_MCP_RECOVERY_COPY,
  CREATE_MCP_REVEAL_TITLE,
  CREATE_MCP_SUBMIT_LABEL,
  CREATE_MCP_SUBMIT_TEST_ID,
  CREATE_MCP_TITLE
} from './constants';
import { TokenCreatedPanel } from './TokenCreatedPanel';
import { useCreateMcpTokenForm } from './useCreateMcpTokenForm';

type CreateMcpTokenModalProps = {
  open: boolean;
  onClose: () => void;
};

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <label className="mb-0.5 flex gap-0.5 text-xs font-medium text-gray-500">
    {children}
    <span className="text-feedback-error">*</span>
  </label>
);

export const CreateMcpTokenModal = ({
  open,
  onClose
}: CreateMcpTokenModalProps) => {
  const { form, submit, close, createdToken, isPending, isError } =
    useCreateMcpTokenForm({ onClose });
  const {
    control,
    formState: { errors, isValid }
  } = form;

  const nameError = errors.name?.message;

  return (
    <Modal
      open={open}
      onCancel={close}
      width={520}
      destroyOnHidden
      closable={!isPending}
      maskClosable={!isPending}
      keyboard={!isPending}
      title={
        <span className="text-xl font-medium">
          {createdToken ? CREATE_MCP_REVEAL_TITLE : CREATE_MCP_TITLE}
        </span>
      }
      footer={
        createdToken ? null : (
          <div className="flex justify-end gap-2">
            <SecondaryButton
              data-testid={CREATE_MCP_CANCEL_TEST_ID}
              onClick={close}
              disabled={isPending}
            >
              {CREATE_MCP_CANCEL_LABEL}
            </SecondaryButton>
            <PrimaryButton
              data-testid={CREATE_MCP_SUBMIT_TEST_ID}
              form={CREATE_MCP_FORM_ID}
              htmlType="submit"
              disabled={!isValid || isPending}
            >
              {CREATE_MCP_SUBMIT_LABEL}
            </PrimaryButton>
          </div>
        )
      }
    >
      {createdToken ? (
        <TokenCreatedPanel token={createdToken} onDone={close} />
      ) : (
        <form
          id={CREATE_MCP_FORM_ID}
          onSubmit={submit}
          data-testid={CREATE_MCP_MODAL_TEST_ID}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col">
            <FieldLabel>{CREATE_MCP_NAME_LABEL}</FieldLabel>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  data-testid={CREATE_MCP_NAME_TEST_ID}
                  placeholder={CREATE_MCP_NAME_PLACEHOLDER}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  status={nameError ? 'error' : undefined}
                  aria-invalid={nameError ? 'true' : undefined}
                  className="h-12 rounded-2xl"
                />
              )}
            />
            {nameError && (
              <p
                data-testid={CREATE_MCP_NAME_ERROR_TEST_ID}
                className="mt-1 text-xs text-feedback-error"
              >
                {nameError}
              </p>
            )}
          </div>

          {isError && (
            <div
              data-testid={CREATE_MCP_ERROR_TEST_ID}
              className="rounded-md bg-feedback-error-surface p-3 text-sm text-content-primary"
            >
              <p className="font-medium">{CREATE_MCP_GENERIC_ERROR}</p>
              <p>{CREATE_MCP_RECOVERY_COPY}</p>
            </div>
          )}
        </form>
      )}
    </Modal>
  );
};
